import React from 'react';
import { Responsive, WidthProvider, Layout } from 'react-grid-layout';
import {
    IDropdownOption,
    PrimaryButton,
    DefaultButton,
    MessageBar,
    MessageBarType,
    Dialog,
    DialogType,
    DialogFooter,
    Text,
    CommandBar,
    ContextualMenuItemType,
    ICommandBarItemProps,
} from '@fluentui/react';
import {
    IDashboardWidget,
    IReportingWidget,
    IWidgetConfiguration,
    getWidgetSizing,
    widgetHasConfiguration,
} from '../../models/Dashboard';
import {
    useGetDashboardsQuery,
    useCreateDashboardMutation,
    useUpdateDashboardMutation,
    useDeleteDashboardMutation,
    useGetAvailableWidgetsQuery,
} from '../../store/endpoints/DashboardEndpoints';
import DashboardWidgetWrapper from './DashboardWidgetWrapper';
import WidgetPickerPanel from './Panels/WidgetPickerPanel';
import WidgetConfigPanel from './Panels/WidgetConfigPanel';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import './Dashboard.css';

const ResponsiveGridLayout = WidthProvider(Responsive);

interface IDashboardGridProps {
    resource: string;
    resourceId?: string;
    commandBarItems?: ICommandBarItemProps[];
}

const GLOBAL_ORG = 'GLOBAL';

const DashboardGrid: React.FC<IDashboardGridProps> = ({ resource, resourceId, commandBarItems }) => {
    const dashboardsQuery = useGetDashboardsQuery({ resource });
    const availableWidgetsQuery = useGetAvailableWidgetsQuery({ resource });
    const [createDashboard] = useCreateDashboardMutation();
    const [updateDashboard] = useUpdateDashboardMutation();
    const [deleteDashboard] = useDeleteDashboardMutation();

    const [selectedDashboardId, setSelectedDashboardId] = React.useState<string | undefined>(undefined);
    const [editMode, setEditMode] = React.useState(false);
    const [widgets, setWidgets] = React.useState<IDashboardWidget[]>([]);
    const [originalWidgets, setOriginalWidgets] = React.useState<IDashboardWidget[]>([]);
    const [widgetPickerOpen, setWidgetPickerOpen] = React.useState(false);
    const [configWidget, setConfigWidget] = React.useState<IDashboardWidget | undefined>(undefined);
    const [showCancelDialog, setShowCancelDialog] = React.useState(false);
    const [showSaveAsDialog, setShowSaveAsDialog] = React.useState(false);
    const [showDeleteDialog, setShowDeleteDialog] = React.useState(false);
    const [showCreateNewDialog, setShowCreateNewDialog] = React.useState(false);
    const [saveAsName, setSaveAsName] = React.useState('');
    const [createNewName, setCreateNewName] = React.useState('');
    const [deletedDefaultMessage, setDeletedDefaultMessage] = React.useState<string | null>(null);

    const dashboards = dashboardsQuery.data?.items || [];
    const availableWidgets = availableWidgetsQuery.data?.items || [];

    const widgetMetaByType = React.useMemo(() => {
        const map: Record<string, IReportingWidget> = {};
        availableWidgets.forEach(w => { map[w.widgetType] = w; });
        return map;
    }, [availableWidgets]);

    // Auto-select the first dashboard if nothing selected
    React.useEffect(() => {
        if (dashboards.length > 0 && !selectedDashboardId) {
            const storageKey = `dashboard-default-${resource}-${resourceId || 'all'}`;
            const savedDefault = localStorage.getItem(storageKey);

            if (savedDefault) {
                const found = dashboards.find(d => d.id === savedDefault);
                if (found) {
                    setSelectedDashboardId(savedDefault);
                    return;
                } else {
                    localStorage.removeItem(storageKey);
                    setDeletedDefaultMessage('Your default dashboard was deleted. Falling back to the default dashboard.');
                }
            }

            const globalDefault = dashboards.find(d => d.organization === GLOBAL_ORG);
            setSelectedDashboardId(globalDefault?.id || dashboards[0]?.id);
        }
    }, [dashboards, selectedDashboardId, resource, resourceId]);

    const selectedDashboard = dashboards.find(d => d.id === selectedDashboardId);
    React.useEffect(() => {
        if (selectedDashboard) {
            setWidgets(selectedDashboard.widgets || []);
            setOriginalWidgets(selectedDashboard.widgets || []);
        }
    }, [selectedDashboard?.id]);

    const isGlobalDashboard = selectedDashboard?.organization === GLOBAL_ORG;

    const dropdownOptions: IDropdownOption[] = dashboards.map(d => ({
        key: d.id!,
        text: `${d.name}${d.organization === GLOBAL_ORG ? ' (Global)' : ''}`,
    }));

    const handleDashboardChange = (_: any, option?: IDropdownOption) => {
        if (option) {
            setSelectedDashboardId(option.key as string);
            setEditMode(false);
        }
    };

    const handleSetAsDefault = () => {
        const storageKey = `dashboard-default-${resource}-${resourceId || 'all'}`;
        localStorage.setItem(storageKey, selectedDashboardId || '');
    };

    const handleEdit = () => {
        setEditMode(true);
        setOriginalWidgets([...widgets]);
    };

    const handleCancel = () => {
        setShowCancelDialog(true);
    };

    const confirmCancel = () => {
        setWidgets([...originalWidgets]);
        setEditMode(false);
        setShowCancelDialog(false);
    };

    const handleSave = async () => {
        if (!selectedDashboardId || isGlobalDashboard) return;
        await updateDashboard({
            resource,
            id: selectedDashboardId,
            request: { widgets },
        });
        setEditMode(false);
    };

    const handleCreateNew = () => {
        setCreateNewName('');
        setShowCreateNewDialog(true);
    };

    const handleConfirmCreateNew = async () => {
        if (!createNewName.trim()) return;
        const result = await createDashboard({
            resource,
            request: {
                name: createNewName.trim(),
                widgets: [],
            },
        }).unwrap();
        setCreateNewName('');
        setShowCreateNewDialog(false);
        setSelectedDashboardId(result.id);
        setEditMode(true);
        setWidgets([]);
    };

    const handleSaveAs = async () => {
        if (!saveAsName.trim()) return;
        const result = await createDashboard({
            resource,
            request: {
                name: saveAsName.trim(),
                widgets,
            },
        }).unwrap();
        setSaveAsName('');
        setShowSaveAsDialog(false);
        setSelectedDashboardId(result.id);
        setEditMode(false);
    };

    const handleDelete = async () => {
        if (!selectedDashboardId || isGlobalDashboard) return;
        await deleteDashboard({ resource, id: selectedDashboardId });
        setSelectedDashboardId(undefined);
        setShowDeleteDialog(false);
    };

    const handleClear = () => {
        setWidgets([]);
    };

    const handleAddWidget = (widgetMeta: IReportingWidget) => {
        const sizing = getWidgetSizing(widgetMeta.displayType);
        const config: IWidgetConfiguration = {};
        if (widgetMeta.hasTimeframeOptions) {
            config.timeframe = 'days';
            config.timeframeValue = 30;
        }
        if (widgetMeta.supportedIntervals && widgetMeta.supportedIntervals.length > 0) {
            config.interval = widgetMeta.supportedIntervals[0];
        }
        if (widgetMeta.metricOptions && widgetMeta.metricOptions.length > 0) {
            config.metric = widgetMeta.metricOptions[0];
        }
        const newWidget: IDashboardWidget = {
            widgetId: `widget-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
            widgetType: widgetMeta.widgetType,
            x: 0,
            y: Infinity,
            w: sizing.defaultW,
            h: sizing.defaultH,
            configuration: Object.keys(config).length > 0 ? config : undefined,
        };
        setWidgets([...widgets, newWidget]);
    };

    const handleRemoveWidget = (widgetId: string) => {
        setWidgets(widgets.filter(w => w.widgetId !== widgetId));
    };

    const handleConfigureWidget = (widget: IDashboardWidget) => {
        setConfigWidget(widget);
    };

    const handleSaveConfig = (config: IWidgetConfiguration) => {
        setWidgets(widgets.map(w =>
            w.widgetId === configWidget?.widgetId
                ? { ...w, configuration: config }
                : w
        ));
        setConfigWidget(undefined);
    };

    const handleLayoutChange = (layout: Layout[]) => {
        if (!editMode) return;
        setWidgets(widgets.map(w => {
            const layoutItem = layout.find(l => l.i === w.widgetId);
            if (layoutItem) {
                return { ...w, x: layoutItem.x, y: layoutItem.y, w: layoutItem.w, h: layoutItem.h };
            }
            return w;
        }));
    };

    const gridLayouts = widgets.map(w => {
        const meta = w.widgetType ? widgetMetaByType[w.widgetType] : undefined;
        const sizing = getWidgetSizing(meta?.displayType);
        return {
            i: w.widgetId!,
            x: w.x,
            y: w.y,
            w: w.w,
            h: w.h,
            minW: sizing.minW,
            minH: sizing.minH,
            maxW: sizing.maxW,
            maxH: sizing.maxH,
            static: !editMode,
        };
    });

    const configWidgetMeta = configWidget?.widgetType ? widgetMetaByType[configWidget.widgetType] : undefined;

    return (
        <div>
            {deletedDefaultMessage && (
                <MessageBar
                    messageBarType={MessageBarType.warning}
                    onDismiss={() => setDeletedDefaultMessage(null)}
                    dismissButtonAriaLabel="Close"
                    style={{ marginBottom: 8 }}
                >
                    {deletedDefaultMessage}
                </MessageBar>
            )}

            {/* Dashboard Toolbar */}
            <CommandBar
                items={commandBarItems || []}
                farItems={[
                    ...(editMode ? [
                        {
                            key: 'addWidget',
                            text: 'Add Widget',
                            iconProps: { iconName: 'Add' },
                            onClick: () => setWidgetPickerOpen(true),
                        },
                        {
                            key: 'clear',
                            text: 'Clear',
                            iconProps: { iconName: 'Clear' },
                            onClick: handleClear,
                        }
                    ] : []),
                    {
                        key: 'dashboardSelector',
                        text: selectedDashboard?.name || 'Select Dashboard',
                        subMenuProps: {
                            items: [
                                ...(dropdownOptions.map(option => ({
                                    iconProps: { iconName: option.key === selectedDashboardId ? 'CheckMark' : 'None' },
                                    key: option.key.toString(),
                                    text: option.text,
                                    onClick: () => handleDashboardChange(null, option),
                                }))),
                                { key: 'divider', itemType: ContextualMenuItemType.Divider },
                                {
                                    key: 'createNew',
                                    text: 'Create New',
                                    iconProps: { iconName: 'Add' },
                                    onClick: handleCreateNew,
                                },
                                {
                                    key: 'setDefault',
                                    text: 'Set as Default',
                                    iconProps: { iconName: 'FavoriteStar' },
                                    onClick: handleSetAsDefault,
                                    disabled: !selectedDashboardId || isGlobalDashboard,
                                },
                                {
                                    key: 'delete',
                                    text: 'Delete',
                                    iconProps: { iconName: 'Delete' },
                                    onClick: () => setShowDeleteDialog(true),
                                    disabled: !selectedDashboardId || isGlobalDashboard,
                                },
                                { key: 'divider2', itemType: ContextualMenuItemType.Divider },
                                (!editMode
                                    ? {
                                        key: 'edit',
                                        text: 'Edit',
                                        iconProps: { iconName: 'Edit' },
                                        onClick: handleEdit,
                                        disabled: !selectedDashboardId,
                                    }
                                    : {
                                        key: 'cancel',
                                        text: 'Cancel',
                                        iconProps: { iconName: 'Cancel' },
                                        onClick: handleCancel,
                                    }
                                ),
                                {
                                    key: 'save',
                                    text: 'Save',
                                    iconProps: { iconName: 'Save' },
                                    onClick: (ev) => {
                                        ev?.preventDefault();
                                        handleSave();
                                    },
                                    disabled: !editMode || isGlobalDashboard || !selectedDashboardId,
                                },
                                {
                                    key: 'saveAs',
                                    text: 'Save As',
                                    iconProps: { iconName: 'SaveAs' },
                                    onClick: (ev) => {
                                        ev?.preventDefault();
                                        setShowSaveAsDialog(true);
                                    },
                                    disabled: !selectedDashboardId,
                                }
                            ],
                        },
                    }
                ]}
                />
            {/* Dashboard Grid */}
            {widgets.length === 0 ? (
                <div style={{
                    textAlign: 'center',
                    padding: '60px 20px',
                    border: '2px dashed #edebe9',
                    borderRadius: 8,
                    color: '#605e5c',
                }}>
                    <Text variant="large">No widgets on this dashboard</Text>
                    <br />
                    {editMode && (
                        <PrimaryButton
                            text="Add a Widget"
                            iconProps={{ iconName: 'Add' }}
                            onClick={() => setWidgetPickerOpen(true)}
                            style={{ marginTop: 16 }}
                        />
                    )}
                </div>
            ) : (
                <ResponsiveGridLayout
                    className="dashboard-grid-layout"
                    layouts={{ lg: gridLayouts }}
                    breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 }}
                    cols={{ lg: 12, md: 10, sm: 6, xs: 4, xxs: 2 }}
                    rowHeight={100}
                    isDraggable={editMode}
                    isResizable={editMode}
                    draggableHandle=".widget-drag-handle"
                    draggableCancel=".widget-no-drag"
                    onLayoutChange={handleLayoutChange}
                    compactType="vertical"
                >
                    {widgets.map(widget => {
                        const meta = widget.widgetType ? widgetMetaByType[widget.widgetType] : undefined;
                        return (
                            <div key={widget.widgetId}>
                                <DashboardWidgetWrapper
                                    widget={widget}
                                    widgetMeta={meta}
                                    editMode={editMode}
                                    onRemove={() => handleRemoveWidget(widget.widgetId!)}
                                    onConfigure={widgetHasConfiguration(meta)
                                        ? () => handleConfigureWidget(widget) : undefined}
                                    resource={resource}
                                    resourceId={resourceId}
                                />
                            </div>
                        );
                    })}
                </ResponsiveGridLayout>
            )}

            {/* Widget Picker Panel */}
            <WidgetPickerPanel
                isOpen={widgetPickerOpen}
                onDismiss={() => setWidgetPickerOpen(false)}
                onAddWidget={(meta) => {
                    handleAddWidget(meta);
                    setWidgetPickerOpen(false);
                }}
                availableWidgets={availableWidgets}
                isLoading={availableWidgetsQuery.isLoading}
            />

            {/* Widget Config Panel */}
            <WidgetConfigPanel
                isOpen={!!configWidget}
                widget={configWidget}
                widgetMeta={configWidgetMeta}
                onDismiss={() => setConfigWidget(undefined)}
                onSave={handleSaveConfig}
            />

            {/* Cancel Confirmation Dialog */}
            <Dialog
                hidden={!showCancelDialog}
                onDismiss={() => setShowCancelDialog(false)}
                dialogContentProps={{
                    type: DialogType.normal,
                    title: 'Discard Changes?',
                    subText: 'Are you sure you want to cancel? All unsaved changes will be lost.',
                }}
            >
                <DialogFooter>
                    <PrimaryButton text="Discard Changes" onClick={confirmCancel} />
                    <DefaultButton text="Keep Editing" onClick={() => setShowCancelDialog(false)} />
                </DialogFooter>
            </Dialog>

            {/* Create New Dialog */}
            <Dialog
                hidden={!showCreateNewDialog}
                onDismiss={() => setShowCreateNewDialog(false)}
                dialogContentProps={{
                    type: DialogType.normal,
                    title: 'Create New Dashboard',
                    subText: 'Enter a name for the new dashboard.',
                }}
            >
                <input
                    type="text"
                    value={createNewName}
                    onChange={(e) => setCreateNewName(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleConfirmCreateNew()}
                    placeholder="Dashboard name"
                    style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: 4 }}
                    autoFocus
                />
                <DialogFooter>
                    <PrimaryButton text="Create" onClick={handleConfirmCreateNew} disabled={!createNewName.trim()} />
                    <DefaultButton text="Cancel" onClick={() => setShowCreateNewDialog(false)} />
                </DialogFooter>
            </Dialog>

            {/* Save As Dialog */}
            <Dialog
                hidden={!showSaveAsDialog}
                onDismiss={() => setShowSaveAsDialog(false)}
                dialogContentProps={{
                    type: DialogType.normal,
                    title: 'Save Dashboard As',
                    subText: 'Enter a name for the new dashboard.',
                }}
            >
                <input
                    type="text"
                    value={saveAsName}
                    onChange={(e) => setSaveAsName(e.target.value)}
                    placeholder="Dashboard name"
                    style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: 4 }}
                />
                <DialogFooter>
                    <PrimaryButton text="Save" onClick={handleSaveAs} disabled={!saveAsName.trim()} />
                    <DefaultButton text="Cancel" onClick={() => setShowSaveAsDialog(false)} />
                </DialogFooter>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog
                hidden={!showDeleteDialog}
                onDismiss={() => setShowDeleteDialog(false)}
                dialogContentProps={{
                    type: DialogType.normal,
                    title: 'Delete Dashboard?',
                    subText: `Are you sure you want to delete "${selectedDashboard?.name}"? This action can be undone by contacting support.`,
                }}
            >
                <DialogFooter>
                    <PrimaryButton text="Delete" onClick={handleDelete} style={{ background: '#a4262c', borderColor: '#a4262c' }} />
                    <DefaultButton text="Cancel" onClick={() => setShowDeleteDialog(false)} />
                </DialogFooter>
            </Dialog>
        </div>
    );
};

export default DashboardGrid;
